import type {
	LocalColumn,
	ScheduleColumn,
	ScheduleColumnType,
} from "./schedule-of-rates.types";

export const COLUMN_TYPE_LABELS: Record<ScheduleColumnType, string> = {
	text: "Text",
	unit_of_measurement: "Unit",
	number: "Number",
	currency: "Currency",
};

export const COLUMN_TYPE_OPTIONS: { label: string; value: ScheduleColumnType }[] = [
	{ value: "text", label: "Text Column" },
	{ value: "unit_of_measurement", label: "Unit of Measurement Column" },
	{ value: "number", label: "Number Column" },
	{ value: "currency", label: "Currency Column" },
];

export const SCHEDULE_CELL_BORDER = "border border-[#EAECF0]";

export const SCHEDULE_SAMPLE_LABELS: Record<ScheduleColumnType, string> = {
	text: "{text}",
	unit_of_measurement: "{unit}",
	number: "{number}",
	currency: "$0.00",
};

let localColumnCounter = 0;

export const generateLocalColumnId = (): string => {
	localColumnCounter += 1;
	return `schedule-col-${localColumnCounter}-${Date.now()}`;
};

export const generateRowId = (): number => {
	return -Math.floor(Math.random() * 1_000_000_000);
};

export const generateSortId = (columns: ScheduleColumn[]): number => {
	if (columns.length === 0) {
		return 1;
	}
	return Math.max(...columns.map((c) => c.sortOrder)) + 1;
};

export const formatCurrencyValue = (value: string): string => {
	const num = parseFloat(value);
	if (isNaN(num)) {
		return value;
	}
	return num.toFixed(2);
};

export const toLocalColumns = (columns: ScheduleColumn[]): LocalColumn[] => {
	return columns.map((col) => ({
		...col,
		localId: generateLocalColumnId(),
	}));
};

export const fromLocalColumns = (columns: LocalColumn[]): ScheduleColumn[] => {
	return columns.map((col, idx) => ({
		id: col.id,
		label: col.label,
		columnType: col.columnType,
		sortOrder: idx + 1,
	}));
};

export interface CellReKeyColumn {
	id: number;
	sortOrder: number;
}

/**
 * Re-key a row's cell values from local column id to the column's sort_order
 * before saving. The `columns` table has no id column — `sort_order` is the
 * only stable key the server can round-trip — but newly-added columns only
 * carry a client-assigned negative placeholder id (see EditScheduleOfRatesModal's
 * `nextNegId`) until save. Saving `values` keyed by that placeholder is what
 * loses data on reload: the server hands columns back keyed by sort_order, so
 * a cell saved under `-1` can never be found again.
 *
 * Existing columns already have `id === sortOrder` (mapColumnFromApi sets it
 * that way on load), so re-keying them is a same-key no-op rather than a
 * double-mapping — this handles a row whose cells are a mix of freshly-typed
 * (negative-id-keyed) and previously-saved (sortOrder-keyed) values without
 * special-casing either.
 *
 * A value whose key matches none of the current columns is simply not
 * carried forward. That's safe: both callers require every column's cell to
 * be non-empty before they even build this payload (useScheduleEditor's
 * `validate()` here, an equivalent per-cell check in
 * CompanyScheduleOfRatesSettings), and an unmatched key makes its column
 * read as empty — so a row with any unmatched key is refused by that
 * validation and never reaches this function.
 */
export const reKeyCellsBySortOrder = (
	columns: CellReKeyColumn[],
	values: Record<number, string>
): Record<number, string> => {
	const cells: Record<number, string> = {};
	columns.forEach((col) => {
		if (values[col.id] !== undefined) {
			cells[col.sortOrder] = values[col.id];
		}
	});
	return cells;
};
